import classNames from 'classnames';
import { motion, useAnimation } from 'framer-motion';
import { useEffect } from 'react';

import { useAccordionContext } from './AccordionContext';
import { panelId } from './panelId';
import renderChildrenWithProps, { Children } from './renderChildrenWithProps';

type Props = {
  children: Children<{ isSelected: boolean }>;
  label?: string;
};

export default function AccordionPanel({ children, label }: Props) {
  const controls = useAnimation();
  const { activeItem } = useAccordionContext();
  const isSelected = activeItem === label;
  const variants = {
    expanded: {
      height: 'unset',
      transition: {
        duration: 0.125,
      },
    },
    collapsed: {
      height: '0px',
      transition: {
        duration: 0.25,
      },
    },
  };

  useEffect(() => {
    if (isSelected) {
      controls.start('expanded');
    } else {
      controls.start('collapsed');
    }
  }, [controls, isSelected]);

  return (
    <motion.div
      id={label ? panelId(label) : undefined}
      role="region"
      aria-hidden={!isSelected}
      // @ts-expect-error - inert is a valid HTML attribute, React types lag behind
      inert={isSelected ? undefined : ''}
      initial="collapsed"
      className={classNames(
        { 'overflow-x-hidden overflow-y-hidden': !isSelected },
        'z-0 mx-3 overflow-hidden rounded-b-xl bg-white',
      )}
      animate={controls}
      transition={{ duration: 0.33 }}
      variants={variants}
    >
      {renderChildrenWithProps(children, { isSelected })}
    </motion.div>
  );
}
